import React, { FC, useState } from "react"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import { GatsbyImage } from "gatsby-plugin-image"

import { ImageType } from "@srcTypes"
import { TestimonialBlockFragmentFragment } from "@graphqlTypes"
import { generateConditionalClassName, getImgixImageData } from "@utils"

import AvatarWithName from "@components/AvatarWithName/AvatarWithName"
import IconButton, { ICON_BUTTON_SIZES } from "@components/IconButton/IconButton"
import TextLink, { TEXT_LINK_SIZES } from "@components/TextLink/TextLink"
import { ICON_TYPES } from "@helpscout/hs-marketing-shared-components"

import "./TestimonialBlockVanity.scss"
import WistiaPopover from "@components/WistiaPopover/WistiaPopover"

type TestimonialBlockVanityProps = {
  hideAvatars: TestimonialBlockFragmentFragment["hideAvatars"],
  items: TestimonialBlockFragmentFragment["items"],
  includeBackground: TestimonialBlockFragmentFragment["includeBackground"],
}

const TestimonialBlockVanity: FC<TestimonialBlockVanityProps> = ({
  hideAvatars,
  includeBackground,
  items,
}) => {
  if (!items) return null

  const [currentItemIndex, setCurrentItemIndex] = useState<number>(0)

  return (
    <div className={`TestimonialBlockVanity item-count--${items.length}`}>
      <div className={`container TestimonialBlockVanity--items`}>
        {items.map((item, i) => {
          if (!item) return null

          const hasImage = !!item.image

          return (
            <div
              className={generateConditionalClassName({
                grid: true,
                "TestimonialBlockVanity--item": true,
                "has-image": hasImage,
                "is-current": currentItemIndex === i,
              })}
              key={item.id}
            >
              {!includeBackground && (
                <GatsbyImage
                  alt=""
                  className="background-gradient"
                  role="presentation"
                  image={getImgixImageData(
                    `/images/components/lp/TestimonialBlock/TestimonialBlockVanity--bg.png`,
                    1600,
                    2.66
                  )}
                />
              )}
              {hasImage && (
                <div className="image-wrapper column column--1-5-M">
                  <GatsbyImage
                    alt=""
                    className="circle"
                    role="presentation"
                    image={getImgixImageData(
                      `/images/components/lp/TestimonialBlock/TestimonialBlockVanity--circle${includeBackground ? "-includeBackground" : ""}.png`,
                      887,
                      1
                    )}
                  />
                  {item.image?.imgixImage?.gatsbyImageData ? (
                    <GatsbyImage
                      alt={item.image?.title || item.title || "Image"}
                      className="image"
                      image={item.image?.imgixImage?.gatsbyImageData}
                    />
                  ) : (
                    <div className="logo-canvas">
                      <img
                        alt={item.image?.title || item.title || "Image"}
                        className="logo"
                        src={item.image?.imgixImage?.url || ""}
                      />
                    </div>
                  )}
                  {item.wistiaId && (
                    <WistiaPopover
                      imageAlt=""
                      id={item.wistiaId}
                    />
                  )}
                </div>

              )}
              <div className={`content column ${hasImage ? "column--5-13-M" : "column--3-11-M"}`}>
                {item.quote?.raw && <blockquote className="color--charcoal-1100">{renderRichText({ raw: item.quote.raw, references: [] })}</blockquote>}
                <footer className={items.length === 1 ? "has-only-one" : ""}>
                  {item.person && item.person.name && item.person.jobTitle && (
                    <AvatarWithName
                      name={item.person.name}
                      bio={item.person.jobTitle}
                      image={hideAvatars ? undefined : (item.person.image as ImageType)}
                    />
                  )}
                  {(items.length === 1 && item.url) && (
                    <TextLink href={item.url} size={TEXT_LINK_SIZES.S}>Read the story</TextLink>
                  )}
                  {(items.length > 1) && (
                    <div className="actions">
                      <div className="controls">
                        {items.length > 1 && (
                          <>
                            <IconButton
                              aria-label="View previous testimonials"
                              disabled={currentItemIndex === 0}
                              iconType={ICON_TYPES["arrow-left"]}
                              onClick={() => setCurrentItemIndex(_index => _index - 1)}
                              size={ICON_BUTTON_SIZES.S}
                            />
                            <IconButton
                              aria-label="View next testimonials"
                              disabled={currentItemIndex === items.length - 1}
                              iconType={ICON_TYPES["arrow-right"]}
                              onClick={() => setCurrentItemIndex(_index => _index + 1)}
                              size={ICON_BUTTON_SIZES.S}
                            />
                          </>
                        )}
                      </div>
                      {item.url && (
                        <TextLink href={item.url} size={TEXT_LINK_SIZES.S}>Read the story</TextLink>
                      )}
                    </div>
                  )}
                </footer>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TestimonialBlockVanity
